import { applicationPaths } from '@core/http/applicationPaths';
import { useApi } from '@core/http/useApi';
import { getPathPayload, isValidationErrorResult, useNotifications } from '@hwm/ui-lib';
import { t } from '@lingui/macro';
import { useCallback } from 'react';

const enum RemovePMZErrorCode {
  AccountIsTopLevel = 'Account.AccountIsTopLevel',
  AccountCannotDeleteAssociatedSitesExist = 'Account.AccountCannotDeleteAssociatedSitesExist',
  AccountCannotDeleteOwnedSitesExist = 'Account.AccountCannotDeleteOwnedSitesExist',
}

const RemovePMZErrorCodeMessageFn = (): Record<RemovePMZErrorCode, string> => ({
  [RemovePMZErrorCode.AccountIsTopLevel]: t`PMZone cannot be removed. Selected PMZ is top level account`,
  [RemovePMZErrorCode.AccountCannotDeleteAssociatedSitesExist]: t`PMZone cannot be removed. Selected PMZone contains associated sites`,
  [RemovePMZErrorCode.AccountCannotDeleteOwnedSitesExist]: t`PMZone cannot be removed. Selected PMZone contains owned sites`,
});

export const useRemovePMZDataSource = () => {
  const { handleSuccess, handleError } = useNotifications();

  const [removePMZone, , loading] = useApi<null, null, getPathPayload<typeof applicationPaths.pmZones.removePMZone>>(
    ({ id }) => applicationPaths.pmZones.removePMZone({ id }),
    'delete'
  );

  const handleRemovePMZone = useCallback(
    (pmzId: number, cb?: () => void) => {
      removePMZone({ id: pmzId })
        .then(() => {
          cb?.();
          handleSuccess(t`Successfully removed PMZone`, t`PMZ`);
        })
        .catch((err) => {
          if (isValidationErrorResult(err)) {
            const errors = err?.data?.flatMap((d) => d?.errors?.map((err) => err?.code as RemovePMZErrorCode));

            const errMsgs = errors
              ?.map((err) => RemovePMZErrorCodeMessageFn()?.[err] ?? null)
              ?.filter(Boolean)
              ?.join(', ');

            handleError(errMsgs, t`PMZ`);
          } else {
            handleError(t`There was a problem while trying to remove PMZone`, t`PMZ`);
          }
        });
    },
    [removePMZone, handleSuccess, handleError]
  );

  return { removePMZone: handleRemovePMZone, loading };
};
